import {Component,Inject,OnDestroy,OnInit} from '@angular/core';
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,MatLegacyDialogRef as MatDialogRef} from "@angular/material/legacy-dialog";
import {ListView} from "@domain/common/list-view";
import {AvanceImputable} from "@domain/avance/avance-imputable";
import {AvancesImputablesListItemComponent} from "./avances-imputables-list-item/avances-imputables-list-item.component";
import {TranslateService} from "@ngx-translate/core";
import {Page} from "@domain/common/http/list-result";
import {Store} from "@ngrx/store";
import {AppState} from "@domain/appstate";
import {SettingsAVState,SettingsGlobalState} from "@domain/settings/settings";
import {TypePortee} from "@domain/workflow/workflow";
import {combineLatest,Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {NDFService} from "../ndf.service";
import {Ndf} from "@domain/ndf/ndf";
import {TypeCodeErreur} from "@domain/common/http/result";
import {handleResult} from "@share/utils/handle-result";
import {ToastrService} from "ngx-toastr";
import {ResultatCalculFinal} from "@domain/ndf/resultat-calcul-final";
import {filterFirstNotNull} from "@share/utils/rxjs-custom-operator";

/**
 * Liste des avances imputables sur une NDF
 *
 * @author Laurent Convert
 * @date 23/04/2026
 */
@Component({
	host: {'data-test-id': 'avances-imputables'},
	selector: 'avances-imputables',
	templateUrl: './avances-imputables.component.html'
})
export class AvancesImputablesComponent implements OnInit,OnDestroy {
	/** Liste des avances imputables */
	listeAvancesImputables: ListView<AvanceImputable,AvancesImputablesListItemComponent>;

	/** Indicateur d'enregistrement */
	isSaving: boolean = false;

	/** Montants à imputer par identifiant d'avance */
	listeMontantImputation: Map<number,number> = new Map();

	/** Indicateur de montant invalide (montant imputé > montant disponible) */
	hasMontantInvalide: boolean = false;

	/** Méthode appelée lors du changement du montant d'une avance */
	onChangeMontant: Subject<{idAvance: number, montant: number}> = new Subject();

	/** Subject pour gérer la désinscription des observables lors de la destruction du composant */
	private destroy$ = new Subject<void>();

	/**
	 * Constructeur
	 *
	 * @param data Données en entrée de la popin
	 * @param dialogRef Référence de la popin
	 * @param translateService Service de traduction
	 * @param toastrService Service de notification
	 * @param store Store de l'application
	 * @param ndfService Le service de gestion des NDF
	 */
	constructor(@Inject(MAT_DIALOG_DATA) public data: { ndf: Ndf },
				private dialogRef: MatDialogRef<AvancesImputablesComponent,ResultatCalculFinal>,
				private translateService: TranslateService,
				private toastrService: ToastrService,
				private store: Store<AppState>,
				private ndfService: NDFService
			) {
	}

	/**
	 * Hook d'initialisation du composant
	 */
	ngOnInit() {
		//Abonnement aux changements de montant à imputer
		this.onChangeMontant
			.pipe(takeUntil(this.destroy$))
			.subscribe(({idAvance, montant}) => {
				//Mise à jour de la Map des montants à imputer
				this.listeMontantImputation.set(idAvance, montant);
				//Vérification si un montant imputé dépasse le montant disponible
				this.checkMontantsValides();
			});

		//Récupération des paramètres depuis le store
		combineLatest([
			this.store.select(state => state.settings?.Global).pipe(filterFirstNotNull()),
			this.store.select(state => state.settings?.[TypePortee.AV]).pipe(filterFirstNotNull())
		]).pipe(
			takeUntil(this.destroy$)
		).subscribe(([globalSettings, avSettings]) => {
			//Initialisation de la liste des avances imputables
			this.initListeAvancesImputables(globalSettings,avSettings);
		});
	}

	/**
	 * Initialise la liste des avances imputables
	 *
	 * @param globalSettings paramètres globaux
	 * @param avSettings Paramètres spécifiques aux avances
	 */
	private initListeAvancesImputables(globalSettings: SettingsGlobalState,avSettings: SettingsAVState) {
		this.listeAvancesImputables = new ListView<AvanceImputable,AvancesImputablesListItemComponent>({
			uri: `/controller/NDF/listeAvancesImputables/${this.data.ndf.idNDF}`,
			title: this.translateService.instant('ndf.avancesImputables.liste'),
			component: AvancesImputablesListItemComponent,
			isFilter: false,
			showPagination: true,
			isSimple: true,
			extraOptions: {
				deviseEntreprise: globalSettings.deviseEntreprise,
				avSettings: avSettings,
				onChangeMontant: this.onChangeMontant
			},
			mapResult: (result: Page<AvanceImputable>) => result?.listeResultats,
		});
	}

	/**
	 * Lance le calcul final à partir des avances sélectionnées avec leurs montants modifiés le cas échéant
	 */
	showCalculsFinaux() {
		//Calcul en cours
		this.isSaving = true;

		//Récupération des avances sélectionnées avec leurs montants modifiés le cas échéant
		const listeAvanceImputation = this.listeAvancesImputables.data.listeResultats
			.filter(av => av.isSelected)
			.map(av => new AvanceImputable({
				...av,
				montant: this.listeMontantImputation.get(av.idAvance) ?? av.montant
			}));

		//Lancement du calcul des montants finaux
		this.ndfService.calculerCalculsFinaux(this.data.ndf,listeAvanceImputation)
			.pipe(handleResult(this.translateService,this.toastrService,null,'global.errors.generic'))
			.subscribe(result => {
				if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
					//Récupération de la liste des modes de remboursement
					const listeModeRemb = result.data.listeModeRemb;

					//Définition du mode de remboursement actuel
					//TODO LCO : reprise AngularJS mais je ne vois pas vraiment l'intérêt de le faire ici plutôt que directement lors du calcul...)
					//le mettre côté back et à supprimer ici si ça ne sert vraiment à rien...
					result.data.calculFinal.idModeRemb = listeModeRemb && listeModeRemb.length > 0 ? listeModeRemb[0].idModeRemb : 0;

					//Affichage du résultat des calculs finaux
					this.ndfService.showCalculsFinaux(result.data)
						.afterClosed()
						.subscribe((isConfirmed) => {
							//Envoie au parent (action WF) le résultat des calculs finaux si l'utilisateur a confirmé, sinon null pour annuler l'action
							this.dialogRef.close(isConfirmed ? result.data : null);
						});
				}
			});
	}

	/**
	 * Vérifie si un montant imputé dépasse le montant disponible
	 */
	checkMontantsValides(): void {
		this.hasMontantInvalide = this.listeAvancesImputables?.data?.listeResultats?.some(
			av => (this.listeMontantImputation.get(av.idAvance) ?? av.montant) > av.montant
		) ?? false;
	}

	/**
	 * Hook de destruction du composant
	 */
	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

}